/**
 * 路由权限 & 按钮权限 Store
 * @file src/Store/permission.js
 * @description 从后端获取菜单权限树，生成动态路由，管理按钮权限集合
 *              — 参照 genostack 项目架构合并
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getMenu, getPermissionTree, getNopermission } from '@/api/user'
import {
  transListToTreeData,
  transListToTreePath,
  setBtnPermission,
  filterAsyncRoutes,
} from '@/utils/routeHelper'

export const usePermissionStore = defineStore('permission', () => {
  // ========== State ==========
  const routes = ref([])           // 已处理的路由树（供菜单渲染）
  const addRoutes = ref([])        // 同上（别名）
  const btnPermission = ref([])    // 按钮权限名称列表
  const route_path_info = ref({})  // 路径 → 权限信息映射

  // ========== Getters ==========

  /** 检查是否拥有某个按钮权限 */
  function hasPermission(btnName) {
    return btnPermission.value.includes(btnName)
  }

  // ========== Actions ==========

  /** 生成登录后的动态路由 */
  async function generateRoutes() {
    const response = await getMenu()
    const accessedRoutes = transListToTreeData(response.permission, -1)
    console.log('当前权限路由:', accessedRoutes)

    const btnPerms = setBtnPermission(response.permissionButton)
    btnPermission.value = [...btnPerms, ...setBtnPermission(response.permission)]
    addRoutes.value = accessedRoutes
    routes.value = accessedRoutes
    route_path_info.value = transListToTreePath(response.permission, -1)
    return accessedRoutes
  }

  /** 生成无需登录的公开路由（未登录态使用） */
  async function generateNopermission() {
    const response = await getNopermission()
    console.log('公开路由:', response)
    const routeList = transListToTreeData(response.permission, -1)
    const btnPerms = setBtnPermission(response.permissionButton)
    btnPermission.value = [...btnPerms, ...setBtnPermission(response.permission)]
    const accessedRoutes = filterAsyncRoutes(routeList, response.permission)
    addRoutes.value = accessedRoutes
    routes.value = accessedRoutes
    route_path_info.value = transListToTreePath(response.permission, -1)
    return accessedRoutes
  }

  /** 获取公共路由（退出登录时重置用） */
  async function getPublicRoutes() {
    const response = await getMenu()
    const publicRouter = response.permission.filter(item => item.is_public)
    const accessedRoutes = transListToTreeData(publicRouter, -1)
    const btnPerms = setBtnPermission(response.permissionButton)
    btnPermission.value = [...btnPerms, ...setBtnPermission(response.permission)]
    addRoutes.value = accessedRoutes
    routes.value = accessedRoutes
    route_path_info.value = transListToTreePath(response.permission, -1)
    return accessedRoutes
  }

  /** 直接获取菜单原始数据 */
  async function GetMenu() {
    const res = await getMenu()
    return res
  }

  /** 直接获取公开路由原始数据 */
  async function GetNopermission() {
    const res = await getNopermission()
    return res
  }

  /** 获取路由树 + 按钮权限树（供菜单管理页使用，flag: 1=菜单 2=按钮） */
  async function GetPermissionTree() {
    const [routeTree, btnTree] = await Promise.all([
      getPermissionTree(1),
      getPermissionTree(2),
    ])
    return { routeTree, btnTree }
  }

  return {
    // state
    routes,
    addRoutes,
    btnPermission,
    route_path_info,
    // getters
    hasPermission,
    // actions
    generateRoutes,
    generateNopermission,
    getPublicRoutes,
    GetMenu,
    GetNopermission,
    GetPermissionTree,
  }
})
